import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Content, SearchOption } from "../../data/registry";
import { matchEasterEgg } from "../../data/easter-eggs";
import { fuzzySearch, FuzzyCandidate } from "../../shared/utils/fuzzyMatch";

interface UseSearchLogicOptions {
  options?: SearchOption[];
  onMobileOpen?: () => void;
  onMobileClose?: () => void;
}

export default function useSearchLogic({
  options = [],
  onMobileOpen,
  onMobileClose,
}: UseSearchLogicOptions) {
  const defaultValue = window.location.pathname.replace(/^\/?/, "");

  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const isKnown = useCallback(
    (q: string) => {
      const ql = q.toLowerCase();
      const known = [
        ...options.flatMap((o) => [o.name, o.value]),
        ...Content.flatMap((c) => [c.category, c.link]),
      ].map((s) => s.toLowerCase());
      return known.includes(ql);
    },
    [options],
  );

  const openLink = (path: string) => {
    if (!path) return;
    const href =
      /^(https?:)?\/\//.test(path) || path.startsWith("/") ? path : `/${path}`;
    // External URLs open in a new tab; internal paths navigate in-place
    if (/^https?:\/\//.test(href)) {
      window.open(href, "_blank", "noopener,noreferrer");
    } else {
      navigate(href);
    }
  };

  const getAllCandidates = useCallback((): FuzzyCandidate[] => {
    const candidates: FuzzyCandidate[] = [];
    options.forEach((o) => {
      candidates.push({ label: o.name, url: `/${o.value}` });
    });
    Content.forEach((c) => {
      candidates.push({ label: c.category, url: `/${c.category}` });
      candidates.push({ label: c.name, url: c.link });
    });
    // Add known routes
    ["about", "projects", "maps", "drive", "resume", "email", "experience", "works", "photos"].forEach(
      (r) => candidates.push({ label: r, url: `/${r}` })
    );
    return candidates;
  }, [options]);

  const search = useCallback(() => {
    const query = value.trim();
    if (!query) return;

    // Check for easter eggs first
    const egg = matchEasterEgg(query);
    if (egg) {
      document.body.classList.add(egg.cssClass);
      setTimeout(() => document.body.classList.remove(egg.cssClass), egg.duration);
      navigate("/all");
      return;
    }

    if (isKnown(query)) {
      const option = options.find(
        (o) => o.name.toLowerCase() === query.toLowerCase()
      );
      if (option) return openLink(option.value);

      const cat = Content.find(
        (c) => c.category.toLowerCase() === query.toLowerCase()
      );
      if (cat) return openLink(cat.link);

      return openLink(query);
    }

    // Try fuzzy search
    const candidates = getAllCandidates();
    const results = fuzzySearch(query, candidates);
    if (results.length > 0) {
      const best = results[0];
      navigate(`/notfound?q=${encodeURIComponent(query)}&suggest=${encodeURIComponent(best.candidate)}&to=${encodeURIComponent(best.url)}`);
      return;
    }

    navigate(`/notfound?q=${encodeURIComponent(query)}`);
  }, [value, options, isKnown, navigate, getAllCandidates]);

  const [activeIndex, setActiveIndex] = useState(-1);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      if (open && activeIndex >= 0) {
        // Select the highlighted option
        const visible = options.filter(
          (o) => !(e.target as HTMLInputElement).closest(".search-box")
            ?.querySelector(`[data-hidden="${o.name}"]`)
        );
        if (visible[activeIndex]) {
          const opt = visible[activeIndex];
          openLink(opt.value);
          setOpen(false);
          return;
        }
      }
      search();
      return;
    }

    if (e.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => Math.max(prev - 1, -1));
    }
  };

  const show = () => {
    setOpen(true);
    setActiveIndex(-1);
    if (window.innerWidth < 768) onMobileOpen?.();
  };

  const hide = () => {
    setTimeout(() => {
      setOpen(false);
      setActiveIndex(-1);
    }, 200);
    if (window.innerWidth < 768) onMobileClose?.();
  };

  return {
    value,
    setValue,
    show,
    hide,
    open,
    activeIndex,
    clear: () => setValue(""),
    onKeyDown,
    search,
  };
}
